import React from 'react';
import { Crown, CheckCircle, ArrowRight, Heart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const SubscriptionCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Get user's subscription tier info — keyed off the canonical tier id when
  // available, falling back to the display-name string. Saved-practice-partner
  // counts mirror app/constants/plans.py (single source of truth).
  const getTierInfo = () => {
    const tierId = (user?.subscription_tier || '').toLowerCase();
    const tierName = (user?.subscription_tier_name || user?.subscription_tier || 'ENTRY').toLowerCase();

    const isMaster = tierId === 'plus_annual' || tierName.includes('master') || tierName.includes('brave') || tierName.includes('annual');
    const isElite = tierId === 'plus_monthly' || tierName.includes('elite') || tierName.includes('together');
    const isChallenger = tierId === 'starter' || tierName.includes('challenger') || tierName.includes('steady') || tierName.includes('starter');

    if (isMaster) {
      return {
        id: 'master',
        name: 'BRAVE',
        price: 349,
        priceLabel: '/year',
        heartsPerMonth: 'Unlimited',
        features: ['38 saved AvA tutors (full specialist library)', 'Voice chat included', 'Achievement NFTs', 'Priority support'],
        badgeStyle: 'bg-amber-100 text-amber-700'
      };
    }
    if (isElite) {
      return {
        id: 'elite',
        name: 'TOGETHER',
        price: 39,
        priceLabel: '/mo',
        heartsPerMonth: 'Unlimited',
        features: ['20 saved AvA tutors (full specialist library)', 'Voice chat included', 'Achievement NFTs', 'Priority support'],
        badgeStyle: 'bg-purple-100 text-purple-700'
      };
    }
    if (isChallenger) {
      return {
        id: 'challenger',
        name: 'STEADY',
        price: 11,
        priceLabel: '/mo',
        heartsPerMonth: 200,
        features: ['8 saved AvA tutors (full specialist library)', 'Unlimited text messages', 'Voice chat included', 'Mood tracking + insights'],
        badgeStyle: 'bg-purple-100 text-purple-700'
      };
    }
    // Default to Free / Entry
    return {
      id: 'entry',
      name: 'FIRST STEPS',
      price: 0,
      priceLabel: '/mo',
      heartsPerMonth: 100,
      features: ['3 saved AvA tutors (specialist library)', '30 text messages / month', 'Basic mood tracking', 'Community access'],
      badgeStyle: 'bg-gray-100 text-gray-700'
    };
  };

  const tierInfo = getTierInfo();
  const heartsBalance = user?.hearts_balance || 0;
  const heartsPerMonth = tierInfo.heartsPerMonth === 'Unlimited' ? heartsBalance : tierInfo.heartsPerMonth;
  const heartsPercentage = tierInfo.heartsPerMonth === 'Unlimited' ? 100 : (heartsBalance / heartsPerMonth) * 100;

  // Determine if user needs to top up (less than 20% remaining)
  const needsTopUp = tierInfo.heartsPerMonth !== 'Unlimited' && heartsPercentage < 20;

  return (
    <div className="card" data-testid="subscription-card">
      <div className="card-header">
        <h2 className="card-title">Subscription</h2>
      </div>

      {/* Tier Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crown size={20} className="text-yellow-500" />
          <span className={`px-3 py-1 rounded-full font-semibold text-sm ${tierInfo.badgeStyle}`}>
            {tierInfo.name}
          </span>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900">
            {tierInfo.price === 0 ? 'Free' : `£${tierInfo.price}`}
          </p>
          {tierInfo.price > 0 && (
            <p className="text-xs text-gray-600">{tierInfo.priceLabel}</p>
          )}
        </div>
      </div>

      {/* Benefits */}
      <div className="mb-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Your Benefits:</p>
        <div className="space-y-2">
          {tierInfo.features.map((feature, index) => (
            <div key={index} className="flex items-start gap-2">
              <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Hearts Progress */}
      <div className={`mb-4 p-3 rounded-lg ${needsTopUp ? 'bg-red-100 border border-red-300' : 'bg-red-50'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-red-500" fill="currentColor" />
            <span className="text-sm font-medium text-gray-700">Hearts Remaining</span>
          </div>
          <span className={`text-sm font-semibold ${needsTopUp ? 'text-red-600' : 'text-red-500'}`}>
            {heartsBalance.toLocaleString()}
            {tierInfo.heartsPerMonth !== 'Unlimited' && ` / ${heartsPerMonth.toLocaleString()}`}
          </span>
        </div>
        {tierInfo.heartsPerMonth !== 'Unlimited' && (
          <div className="w-full bg-red-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                needsTopUp ? 'bg-red-600' : 'bg-gradient-to-r from-red-400 to-red-600'
              }`}
              style={{ width: `${Math.min(heartsPercentage, 100)}%` }}
            />
          </div>
        )}
        {needsTopUp && (
          <p className="text-xs text-red-600 font-medium mt-2">
            Running low! Top up your Hearts to continue voice conversations.
          </p>
        )}
        {tierInfo.heartsPerMonth === 'Unlimited' && (
          <p className="text-xs text-green-600 font-medium mt-1">
            Voice chat is included with your {tierInfo.name} plan.
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        {/* Top Up Hearts Button — Hidden for Elite/Master (unlimited hearts) */}
        {tierInfo.id !== 'elite' && tierInfo.id !== 'master' && (
          <button
            className={`w-full py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
              needsTopUp
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
            }`}
            onClick={() => navigate('/hearts')}
            data-testid="topup-hearts-button"
          >
            <Heart size={16} fill="currentColor" />
            <span>{needsTopUp ? 'Top Up Hearts Now' : 'Buy More Hearts'}</span>
          </button>
        )}

        {/* Manage/Upgrade Subscription Button */}
        <button
          className="w-full py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
          onClick={() => navigate('/pricing')}
          data-testid="manage-subscription-button"
        >
          <span>{(tierInfo.id === 'elite' || tierInfo.id === 'master') ? 'View Subscription' : 'Upgrade Plan'}</span>
          <ArrowRight size={16} />
        </button>
      </div>

      {tierInfo.id !== 'elite' && tierInfo.id !== 'master' && (
        <p className="text-xs text-center text-gray-600 mt-3">
          Upgrade for more practice time and exclusive real-world challenge packs!
        </p>
      )}
    </div>
  );
};

export default SubscriptionCard;
